import { APP_VERSION } from './version';

const VERSION_CHECK_INTERVAL = 30000; // Check every 30 seconds
const VERSION_STORAGE_KEY = 'app_version';

export class CacheService {
  private static instance: CacheService;
  private checkInterval: NodeJS.Timeout | null = null;
  private onUpdateCallback: (() => void) | null = null;

  private constructor() {}

  public static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
    }
    return CacheService.instance;
  }

  /**
   * Initialize the cache service with version checking
   */
  public init(onUpdateAvailable?: () => void): void {
    this.onUpdateCallback = onUpdateAvailable || null;
    
    // Store the current version
    this.storeCurrentVersion();
    
    // Start periodic version checking
    this.startVersionChecking();
    
    console.log(`🚀 Cache Service initialized - Current version: ${APP_VERSION}`);
  }

  /**
   * Start checking for version updates periodically
   */
  private startVersionChecking(): void {
    this.checkInterval = setInterval(() => {
      this.checkForUpdates();
    }, VERSION_CHECK_INTERVAL);
  }

  /**
   * Stop version checking
   */
  public stop(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Store the current version in localStorage
   */
  private storeCurrentVersion(): void {
    try {
      localStorage.setItem(VERSION_STORAGE_KEY, APP_VERSION);
    } catch (error) {
      console.warn('Failed to store version in localStorage:', error);
    }
  }

  /**
   * Get the stored version from localStorage
   */
  private getStoredVersion(): string | null {
    try {
      return localStorage.getItem(VERSION_STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to get version from localStorage:', error);
      return null;
    }
  }

  /**
   * Check if there's a version mismatch (indicating a new deployment)
   */
  private hasVersionChanged(): boolean {
    const storedVersion = this.getStoredVersion();
    return storedVersion !== null && storedVersion !== APP_VERSION;
  }

  /**
   * Check for updates by comparing stored version with current app version
   */
  private async checkForUpdates(): Promise<void> {
    try {
      // Check if the current version differs from stored version
      if (this.hasVersionChanged()) {
        console.log(`🔄 Version change detected: ${this.getStoredVersion()} → ${APP_VERSION}`);
        this.handleUpdateDetected();
        return;
      }
    } catch (error) {
      // Silently fail - version checking is not critical
      console.debug('Version check failed:', error);
    }
  }

  /**
   * Handle when an update is detected
   */
  private handleUpdateDetected(): void {
    this.stop(); // Stop checking to avoid multiple prompts
    
    if (this.onUpdateCallback) {
      this.onUpdateCallback();
    } else {
      // Default behavior: show a simple alert
      this.showDefaultUpdatePrompt();
    }
  }

  /**
   * Show a default update prompt
   */
  private showDefaultUpdatePrompt(): void {
    const shouldReload = window.confirm(
      '🔄 A new version of the application is available!\n\n' +
      'Click OK to refresh and get the latest updates, or Cancel to continue with the current version.'
    );

    if (shouldReload) {
      this.forceReload();
    }
  }

  /**
   * Force reload the page with cache clearing
   */
  public forceReload(): void {
    // Clear localStorage version to ensure fresh start
    try {
      localStorage.removeItem(VERSION_STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear version from localStorage:', error);
    }

    // Force reload with cache clearing
    window.location.reload();
  }

  /**
   * Manually trigger an update check
   */
  public async checkNow(): Promise<void> {
    await this.checkForUpdates();
  }

  /**
   * Get the current app version
   */
  public getCurrentVersion(): string {
    return APP_VERSION;
  }

  /**
   * Clear all cached data and reload
   */
  public clearCacheAndReload(): void {
    try {
      // Clear localStorage
      localStorage.clear();
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // If service worker is available, clear its cache
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            registration.unregister();
          });
        });
      }

      // Clear browser cache if possible
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            caches.delete(name);
          });
        });
      }
    } catch (error) {
      console.warn('Failed to clear some caches:', error);
    }

    // Force reload
    this.forceReload();
  }
}

// Export a singleton instance
export const cacheService = CacheService.getInstance();
